import React, { useState, useEffect } from 'react';
import { rolService } from '../../services/rolService';
import DataTable from 'react-data-table-component';
import { 
  Box, TextField, Button, IconButton, Modal,
  Typography, Paper, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../styles/AppStyle.css';

const RolePage = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await rolService.getAllRoles();
      setRoles(data);
      setLoading(false);
    } catch (err) {
      setError('Error al cargar los roles');
      setLoading(false);
    }
  };

  const getStatusText = (idStatus) => {
    return idStatus === 1 ? 'Activo' : 'Inactivo';
  };
  const today = new Date();

  const handleSaveRol = async () => {
    var roleDTO = {
      name,
      description
    }
    try {
      if (selectedRole) {
        await rolService.editRole(selectedRole.id, roleDTO);
      } else {
        await rolService.createRole(roleDTO);
      }
      setIsModalOpen(false);
      fetchRoles();
      toast.success('Rol guardado con éxito');
    } catch (err) {
      setError('Error al guardar el rol');
    }
  };

  const handleEdit = (role) => {
    setSelectedRole(role);    
    setName(role.name);
    setDescription(role.description);
    setIsModalOpen(true);
  };

  const handleDelete = (role) => {
    setRoleToDelete(role);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    await rolService.deactivateRole(roleToDelete.id)
    fetchRoles();
    setIsDeleteDialogOpen(false);
  };

  const handleViewDetail = (role) => {
    setSelectedRole(role);
    setIsDetailModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedRole(null);
    setName('');
    setDescription('');
    setIsModalOpen(true);
  };

  const columns = [
    {
      name: 'ID',
      selector: row => row.id,
      sortable: true,
    },
    {
      name: 'Descripción',
      selector: row => row.description,
      sortable: true,
      grow: 2,
    },
    {
      name: 'Estado',
      selector: row => getStatusText(row.idStatus),
      sortable: true,
      cell: row => (
        <span className={`apple-badge ${row.idStatus === 1 ? 'apple-badge-success' : 'apple-badge-error'}`}>
          {getStatusText(row.idStatus)}
        </span>
      ),
    },
    {
      name: 'Acciones',
      cell: row => (
        <Box>
          <IconButton onClick={() => handleViewDetail(row)} size="small" className="apple-button apple-tooltip">
            <VisibilityIcon fontSize="small" />
            <span className="tooltiptext">Ver Detalle</span>
          </IconButton>
          <IconButton onClick={() => handleEdit(row)} size="small" className="apple-button apple-tooltip">
            <EditIcon fontSize="small" />
            <span className="tooltiptext">Editar</span>
          </IconButton>
          <IconButton onClick={() => handleDelete(row)} size="small" className="apple-button apple-tooltip">
            <RemoveCircleOutlineIcon fontSize="small" color='error'/>
            <span className="tooltiptext">Desactivar</span>
          </IconButton>
        </Box>
      ),
      width: '200px',
    },
  ];

  const filteredItems = roles.filter(
    item => (item.description && item.description.toLowerCase().includes(filterText.toLowerCase())) ||
            (item.name && item.name.toLowerCase().includes(filterText.toLowerCase()))
  );

  const customStyles = {
    table: {
      style: {
        minWidth: '100%',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#F2F2F7',
        borderBottom: 'none',
      },
    },
    headCells: {
      style: {
        fontSize: '12px',
        fontWeight: '600',
        color: '#86868B',
        textTransform: 'uppercase',
      },
    },
    cells: {
      style: {
        fontSize: '14px',
        color: '#1D1D1F',
      },
    },
  };

  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <Box className="apple-card" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 2 }}>
        <div className="apple-search-container">
          <SearchIcon className="apple-search-icon" />
          <input
            type="text"
            placeholder="Buscar por descripción"
            onChange={e => setFilterText(e.target.value)}
            className="apple-search-input"
          />
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          className="apple-button"
        >
          Agregar Rol
        </Button>
      </Box>
    );
  }, [filterText]);

  return (
    <div className="apple-container">
      <Paper className="apple-card">
  
        <DataTable
          columns={columns}
          data={filteredItems}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 30]}
          subHeader
          subHeaderComponent={subHeaderComponentMemo}
          progressPending={loading}
          persistTableHead
          customStyles={customStyles}
        />

      </Paper>

      {/* Modal para editar/crear rol */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
         <Box className="apple-modal" sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
        }}>
          <Typography id="modal-modal-title" className="apple-modal-title">
            {selectedRole ? 'Editar Rol' : 'Agregar Nuevo Rol'}
          </Typography>
          <div className="apple-modal-content">
            <TextField
              label="Descripción Corta"
              variant="outlined"
              fullWidth
              className="apple-input"
              defaultValue={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              label="Descripción"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              className="apple-input"
              defaultValue={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="apple-modal-footer">
            <Button onClick={() => setIsModalOpen(false)} className="apple-button">
              Cancelar
            </Button>
            <Button variant="contained" className="apple-button" onClick={() => handleSaveRol()}>
              {selectedRole ? 'Guardar Cambios' : 'Crear Rol'}
            </Button>
          </div>
        </Box>
      </Modal>

      {/* Modal para ver detalles */}
      <Modal
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="apple-modal" sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
        }}>
          <Typography id="modal-modal-title" className="apple-modal-title">
            Detalles del Rol
          </Typography>
          <div className="apple-modal-content">
            <TextField
              label="ID"
              fullWidth
              value={selectedRole?.id}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              label="Descripción Corta"
              fullWidth
              value={selectedRole?.name}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              label="Descripción"
              fullWidth
              value={selectedRole?.description}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              label="Estado"
              fullWidth
              value={getStatusText(selectedRole?.idStatus)}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />
            <TextField
              label="Fecha de Creación"
              fullWidth
              value={new Date(selectedRole?.create_At).toLocaleString()}
              margin="normal"
              InputProps={{
                readOnly: true,
              }}
            />

            </div>


{/*           {selectedRole && (
            <div className="apple-modal-content">
              <div className="apple-modal-detail-item">
                <div className="apple-modal-detail-label">ID:</div>
                <div className="apple-modal-detail-value">{selectedRole.id}</div>
              </div>
              <div className="apple-modal-detail-item">
                <div className="apple-modal-detail-label">Descripción Corta:</div>
                <div className="apple-modal-detail-value">{selectedRole.name}</div>
              </div>
              <div className="apple-modal-detail-item">
                <div className="apple-modal-detail-label">Descripción:</div>
                <div className="apple-modal-detail-value">{selectedRole.description}</div>
              </div>
              <div className="apple-modal-detail-item">
                <div className="apple-modal-detail-label">Estado:</div>
                <div className="apple-modal-detail-value">{getStatusText(selectedRole.idStatus)}</div>
              </div>
              <div className="apple-modal-detail-item">
                <div className="apple-modal-detail-label">Fecha de Creación:</div>
                <div className="apple-modal-detail-value">{new Date(selectedRole.create_At).toLocaleString()}</div>
              </div>
              <div className="apple-modal-detail-item">
                <div className="apple-modal-detail-label">Última Actualización:</div>
                <div className="apple-modal-detail-value">{new Date(selectedRole.update_At).toLocaleString()}</div>
              </div>
            </div>
          )} */}
          <div className="apple-modal-footer">
            <Button onClick={() => setIsDetailModalOpen(false)} className="apple-button">
              Cerrar
            </Button>
          </div>
        </Box>
      </Modal>

      {/* Diálogo de confirmación para eliminar */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmar eliminación"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Está seguro de que desea desactivar este rol?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} className="apple-button">Cancelar</Button>
          <Button onClick={confirmDelete} className="apple-button" autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      <footer >
        <p>© {today.getFullYear()} Generador de Contratos. Todos los derechos reservados Grupo Defensa. Desarrollado por Gerencia IT </p>
      </footer>
    </div>
  );
};

export default RolePage;